import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PostImage from '../components/PostImage';
import { useTheme } from '../context/ThemeContext';
import { useLanguage, useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { addReaction, addCommentReaction, addComment, deleteComment } from '../lib/api';
import { useCanMessage } from '../lib/queries';
import { 
  ArrowLeft, Heart, MessageCircle, Grid3X3, Crown, Lock,
  Users, UserPlus, UserCheck, Share2, MoreHorizontal, Image, LayoutGrid,
  Scale, Utensils, Dumbbell, Trophy, MapPin, Globe, X, Copy, Check, Send, Trash2, UserX, AlertTriangle, Loader2,
  ChevronLeft, ChevronRight, Sparkles, Award, Cake, Languages, Users as UsersIcon
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { vibrateTap } from '../utils/vibration';
import { countryCodeToFlag } from '../utils/countryFlag';

const API = API_URL;

// Reaction emojis
const REACTION_EMOJIS = ['💪', '🔥', '❤️'];

// Post type config for feed-style display
const POST_TYPE_CONFIG = {
  peso: { icon: Scale, labelKey: 'Peso', badge: 'badge-peso' },
  pasto: { icon: Utensils, labelKey: 'Pasto', badge: 'badge-pasto' },
  esercizio: { icon: Dumbbell, labelKey: 'Esercizio', badge: 'badge-esercizio' },
  motivazione: { icon: Heart, labelKey: 'Motivazione', badge: 'badge-motivazione' },
  traguardo: { icon: Trophy, labelKey: 'Traguardo', badge: 'badge-traguardo' },
  camminata: { icon: MapPin, labelKey: 'Camminata', badge: 'badge-camminata' }
};

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'images' - Default to 'all'
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState(null);
  const [showShareSheet, setShowShareSheet] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // When the user taps the avatar, show it full-screen.
  const [showAvatarZoom, setShowAvatarZoom] = useState(false);
  
  // Swipe state for fullscreen gallery
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  // Comment management state
  const [expandedComments, setExpandedComments] = useState({});
  const [commentTexts, setCommentTexts] = useState({});
  const [sendingComment, setSendingComment] = useState({});
  const [longPressComment, setLongPressComment] = useState(null);
  const longPressTimerRef = useRef(null);

  const loadProfile = useCallback(async () => {
    try {
      // Check if blocked first
      const blockRes = await axios.get(`${API}/api/social/is-blocked/${userId}`, { withCredentials: true });
      if (blockRes.data?.blocked_me) {
        // User has blocked us - show limited view
        setIsBlocked(true);
        setLoading(false);
        return;
      }
      setIsBlocked(blockRes.data?.blocked_by_me || false);
      
      const res = await axios.get(`${API}/api/social/profile/${userId}`, { withCredentials: true });
      setProfile(res.data);
      
      if (res.data.can_view_posts) {
        const postsRes = await axios.get(`${API}/api/social/profile/${userId}/posts?limit=30`, { withCredentials: true });
        setPosts(postsRes.data.posts || []);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadProfile();
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    // Also scroll the main container if it exists
    const mainContainer = document.querySelector('.h-screen.overflow-y-auto');
    if (mainContainer) {
      mainContainer.scrollTo(0, 0);
    }
  }, [loadProfile]);

  // Block/Unblock user
  const handleBlockUser = async () => {
    setBlockLoading(true);
    try {
      if (isBlocked) {
        await axios.post(`${API}/api/social/unblock`, { user_id: userId }, { withCredentials: true });
        setIsBlocked(false);
        loadProfile(); // Reload to get updated data
      } else {
        await axios.post(`${API}/api/social/block`, { user_id: userId }, { withCredentials: true });
        setIsBlocked(true);
        setProfile(prev => ({ ...prev, is_following: false }));
      }
      setShowBlockConfirm(false);
    } catch (err) {
      console.error('Block error:', err);
    } finally {
      setBlockLoading(false);
    }
  };

  const handleFollow = async () => {
    if (followLoading) return;
    setFollowLoading(true);

    // Haptic: light tap confirms the toggle registered.
    vibrateTap();

    // OPTIMISTIC UPDATE — flip is_following + adjust follower count immediately
    // so the button state feels instant. Revert on error.
    const wasFollowing = profile.is_following;
    setProfile((prev) => ({
      ...prev,
      is_following: !wasFollowing,
      followers_count: Math.max(0, (prev?.followers_count || 0) + (wasFollowing ? -1 : 1)),
    }));

    try {
      if (wasFollowing) {
        await axios.post(`${API}/api/social/unfollow`, { user_id: userId }, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/social/follow`, { user_id: userId }, { withCredentials: true });
      }
      // Reload in background so any server-side counters (e.g. mutual follow badges) stay correct.
      loadProfile().catch(() => {});
    } catch (err) {
      console.error('Follow error:', err);
      // Revert optimistic update
      setProfile((prev) => ({
        ...prev,
        is_following: wasFollowing,
        followers_count: Math.max(0, (prev?.followers_count || 0) + (wasFollowing ? 1 : -1)),
      }));
    } finally {
      setFollowLoading(false);
    }
  };

  const loadFollowers = async () => {
    try {
      const res = await axios.get(`${API}/api/social/followers/${userId}`, { withCredentials: true });
      setFollowersList(res.data.followers || []);
      setShowFollowers(true);
    } catch (err) {
      console.error('Error loading followers:', err);
    }
  };

  const loadFollowing = async () => {
    try {
      const res = await axios.get(`${API}/api/social/following/${userId}`, { withCredentials: true });
      setFollowingList(res.data.following || []);
      setShowFollowing(true);
    } catch (err) {
      console.error('Error loading following:', err);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatPostTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Ora';
    if (minutes < 60) return `${minutes} min fa`;
    if (hours < 24) return `${hours} ore fa`;
    if (days < 7) return `${days} giorni fa`;
    
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };

  // Share profile functionality
  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/app/utente/${userId}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Profilo di ${profile.display_name}`,
          text: `Guarda il profilo di ${profile.display_name} su Slimmy!`,
          url: profileUrl,
        });
      } else {
        setShowShareSheet(true);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setShowShareSheet(true);
      }
    }
  };

  const copyProfileLink = () => {
    const profileUrl = `${window.location.origin}/app/utente/${userId}`;
    try {
      navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowShareSheet(false);
      }, 1500);
    } catch (err) {
      console.warn('Clipboard API not available:', err);
    }
  };

  // Update a single post in the posts array
  const updatePost = (postId, updater) => {
    setPosts(prev => prev.map(p => p.post_id === postId ? updater(p) : p));
  };

  // Handle reaction on post
  const handleReaction = async (postId, emoji) => {
    const post = posts.find(p => p.post_id === postId);
    if (!post || !user?.user_id) return;
    
    const currentReactions = post.reactions || {};
    const userCurrentEmoji = REACTION_EMOJIS.find(e => 
      currentReactions[e]?.includes(user.user_id)
    );
    const isSameEmoji = userCurrentEmoji === emoji;
    
    // Optimistic update
    const optimisticUpdate = (p) => {
      const newReactions = { ...p.reactions };
      if (userCurrentEmoji && !isSameEmoji) {
        newReactions[userCurrentEmoji] = (newReactions[userCurrentEmoji] || [])
          .filter(id => id !== user.user_id);
      }
      if (isSameEmoji) {
        newReactions[emoji] = (newReactions[emoji] || []).filter(id => id !== user.user_id);
      } else {
        newReactions[emoji] = [...(newReactions[emoji] || []), user.user_id];
      }
      return { ...p, reactions: newReactions };
    };
    
    updatePost(postId, optimisticUpdate);
    
    try {
      const res = await addReaction(postId, emoji);
      updatePost(postId, () => res.data);
    } catch (err) {
      console.error('Reaction error:', err);
    }
  };

  // Handle reaction on comment
  const handleCommentReaction = async (postId, commentId, emoji) => {
    const post = posts.find(p => p.post_id === postId);
    if (!post || !user?.user_id) return;
    const comment = (post.comments || []).find(c => c.comment_id === commentId);
    if (!comment) return;

    const currentReactions = comment.reactions || {};
    const userCurrentEmoji = REACTION_EMOJIS.find(e =>
      currentReactions[e]?.includes(user.user_id)
    );
    const isSameEmoji = userCurrentEmoji === emoji;

    updatePost(postId, (p) => ({
      ...p,
      comments: (p.comments || []).map(c => {
        if (c.comment_id !== commentId) return c;
        const newReactions = { ...(c.reactions || {}) };
        if (userCurrentEmoji && !isSameEmoji) {
          newReactions[userCurrentEmoji] = (newReactions[userCurrentEmoji] || [])
            .filter(id => id !== user.user_id);
        }
        if (isSameEmoji) {
          newReactions[emoji] = (newReactions[emoji] || []).filter(id => id !== user.user_id);
        } else {
          newReactions[emoji] = [...(newReactions[emoji] || []), user.user_id];
        }
        return { ...c, reactions: newReactions };
      }),
    }));

    try {
      const res = await addCommentReaction(postId, commentId, emoji);
      updatePost(postId, () => res.data);
    } catch (err) {
      console.error('Comment reaction error:', err);
    }
  };

  // Handle adding comment
  const handleAddComment = async (postId) => {
    const text = commentTexts[postId]?.trim();
    if (!text || sendingComment[postId]) return;
    
    setSendingComment(prev => ({ ...prev, [postId]: true }));
    
    try {
      const res = await addComment(postId, text);
      updatePost(postId, () => res.data);
      setCommentTexts(prev => ({ ...prev, [postId]: '' }));
    } catch (err) {
      console.error('Comment error:', err);
    } finally {
      setSendingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Handle deleting comment
  const handleDeleteComment = async (postId, commentId) => {
    // Optimistic update
    updatePost(postId, (p) => ({
      ...p,
      comments: (p.comments || []).filter(c => c.comment_id !== commentId)
    }));
    setLongPressComment(null);
    
    try {
      const res = await deleteComment(postId, commentId);
      updatePost(postId, () => res.data);
    } catch (err) {
      console.error('Delete comment error:', err);
    }
  };

  // Long press handlers for comment deletion
  const handleCommentLongPressStart = (comment, postId) => {
    if (comment.user_id !== user?.user_id) return;
    longPressTimerRef.current = setTimeout(() => {
      setLongPressComment({ ...comment, postId });
    }, 500);
  };

  const handleCommentLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  // Get reaction count and check if user reacted
  const getReactionCount = (post, emoji) => post.reactions?.[emoji]?.length || 0;
  const hasReacted = (post, emoji) => post.reactions?.[emoji]?.includes(user?.user_id);

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="w-8 h-8 border-2 border-[#E8334A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex items-center justify-center`}>
        <p className={isDark ? 'text-white/60' : 'text-gray-500'}>Utente non trovato</p>
      </div>
    );
  }

  // Filter posts based on view mode
  const filteredPosts = viewMode === 'images' 
    ? posts.filter(p => p.image_url) 
    : posts;
    
  // Get all posts with images for gallery navigation
  const postsWithImages = posts.filter(p => p.image_url);
  
  // Find index of image in gallery
  const openGalleryAtIndex = (imageUrl) => {
    const index = postsWithImages.findIndex(p => p.image_url === imageUrl);
    if (index !== -1) {
      setFullscreenImageIndex(index);
    }
  };
  
  // Handle swipe gestures
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left - next image
        setFullscreenImageIndex(prev => 
          prev < postsWithImages.length - 1 ? prev + 1 : prev
        );
      } else {
        // Swipe right - previous image
        setFullscreenImageIndex(prev => prev > 0 ? prev - 1 : prev);
      }
    }
  };

  return (
    <div className={`min-h-full ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`}>
      {/* Fixed Header */}
      <div 
        className={`fixed top-0 left-0 right-0 z-30 ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <h1 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {profile.display_name}
          </h1>
          <div className="relative">
            <button 
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2 -mr-2"
            >
              <MoreHorizontal className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
            </button>
            
            {/* Dropdown Menu */}
            {showMoreMenu && (
              <>
                <div 
                  className="fixed inset-0 z-30" 
                  onClick={() => setShowMoreMenu(false)} 
                />
                <div className={`absolute right-0 top-10 z-40 w-48 rounded-xl shadow-lg ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  {!profile.is_own_profile && (
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        setShowBlockConfirm(true);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left ${
                        isBlocked 
                          ? isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'
                          : 'text-red-500 hover:bg-red-500/10'
                      }`}
                    >
                      <UserX className="w-5 h-5" />
                      {isBlocked ? (t('social.unblock') || 'Sblocca') : (t('social.block') || 'Blocca')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div style={{ height: 'calc(env(safe-area-inset-top, 0px) + 64px)' }} />

      {/* Profile Info */}
      <div className="px-4 pt-5 pb-5">
        <div className="flex items-start gap-5">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                const src = profile.profile_photo || profile.picture || profile.profilePhoto || profile.avatar;
                if (src) setShowAvatarZoom(true);
              }}
              className={`w-20 h-20 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'} ring-2 ${profile.is_champion ? 'ring-amber-400 ring-offset-2 ring-offset-amber-400/20' : profile.is_premium ? 'ring-amber-400' : isDark ? 'ring-white/20' : 'ring-gray-300'} block`}
              aria-label={t('profile.viewPhoto', 'Visualizza foto')}
              data-testid="user-profile-avatar"
            >
              {(profile.profile_photo || profile.picture || profile.profilePhoto || profile.avatar) ? (
                <img 
                  src={profile.profile_photo || profile.picture || profile.profilePhoto || profile.avatar} 
                  alt={profile.display_name || ''} 
                  className="w-full h-full object-cover" 
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`w-full h-full items-center justify-center ${(profile.profile_photo || profile.picture || profile.profilePhoto || profile.avatar) ? 'hidden' : 'flex'}`}
              >
                <span className={`text-2xl font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                  {profile.display_name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
            </button>
            {/* Champion badge takes priority over Premium */}
            {profile.is_champion ? (
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg ring-2 ring-white">
                <Trophy className="w-4 h-4 text-white" />
              </div>
            ) : profile.is_premium ? (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
                <Crown className="w-3.5 h-3.5 text-amber-900" />
              </div>
            ) : null}
          </div>

          {/* Stats */}
          <div className="flex-1 flex justify-around">
            <div className="text-center">
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatNumber(profile.post_count || 0)}
              </p>
              <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('social.posts')}
              </p>
            </div>
            <button onClick={loadFollowers} className="text-center">
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatNumber(profile.followers_count || 0)}
              </p>
              <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('social.followers')}
              </p>
            </button>
            <button onClick={loadFollowing} className="text-center">
              <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatNumber(profile.following_count || 0)}
              </p>
              <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('social.followingTab')}
              </p>
            </button>
          </div>
        </div>

        {/* Name & Bio */}
        <div className="mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {profile.display_name}
            </h2>
            {profile.is_champion && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white uppercase tracking-wide flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Champion
              </span>
            )}
            {profile.is_private && (
              <Lock className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
            )}
          </div>
          
          {/* Champion Achievement Banner */}
          {profile.is_champion && profile.champion_achievement && (
            <div className={`mt-3 p-3 rounded-xl ${isDark ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/20' : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Award className="w-4 h-4 text-amber-500" />
                <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  {profile.champion_title || 'Coach della Community'}
                </span>
              </div>
              <p className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {profile.champion_achievement}
              </p>
              {profile.champion_since && (
                <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  Champion dal {new Date(profile.champion_since).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>
          )}
          
          {profile.bio && (
            <p className={`text-sm mt-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              {profile.bio}
            </p>
          )}

          {/* Public info chips — age, sex, country, language. Each one is
              stripped server-side when the profile owner hides it via
              Privacy profilo, so we can render them unconditionally. */}
          {(profile.age || profile.sex || profile.country || profile.language) && (
            <div className={`flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-testid="user-profile-meta">
              {profile.age && (
                <span className="inline-flex items-center gap-1.5" data-testid="user-profile-age">
                  <Cake className="w-3.5 h-3.5" />
                  {profile.age} {t('social.yearsOld', 'anni')}
                </span>
              )}
              {profile.sex && (
                <span className="inline-flex items-center gap-1.5" data-testid="user-profile-sex">
                  <UsersIcon className="w-3.5 h-3.5" />
                  {profile.sex === 'M' || profile.sex === 'm' || profile.sex === 'male'
                    ? t('social.male', 'Uomo')
                    : profile.sex === 'F' || profile.sex === 'f' || profile.sex === 'female'
                      ? t('social.female', 'Donna')
                      : profile.sex}
                </span>
              )}
              {profile.country && (
                <span className="inline-flex items-center gap-1.5" data-testid="user-profile-country">
                  {countryCodeToFlag(profile.country) ? (
                    <span className="text-base leading-none">{countryCodeToFlag(profile.country)}</span>
                  ) : (
                    <MapPin className="w-3.5 h-3.5" />
                  )}
                  {profile.country.toUpperCase()}
                </span>
              )}
              {profile.language && (
                <span className="inline-flex items-center gap-1.5 uppercase" data-testid="user-profile-language">
                  <Languages className="w-3.5 h-3.5" />
                  {profile.language}
                </span>
              )}
            </div>
          )}
          
          {/* Total Likes */}
          {profile.total_likes > 0 && (
            <div className="flex items-center gap-1 mt-2">
              <Heart className="w-4 h-4 text-[#E8334A]" fill="#E8334A" />
              <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {formatNumber(profile.total_likes)} {t('social.likes')} totali
              </span>
            </div>
          )}
        </div>

        {/* Badges */}
        {profile.badges?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {profile.badges.slice(0, 5).map((badge, i) => (
              <div 
                key={badge.badge_code || `badge-${i}`}
                className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 ${isDark ? 'bg-white/10 text-white/80' : 'bg-gray-100 text-gray-700'}`}
              >
                <span>{badge.emoji || '🏆'}</span>
                <span>{badge.name || badge.badge_code?.replace(/_/g, ' ')}</span>
              </div>
            ))}
            {profile.badges.length > 5 && (
              <div className={`px-2.5 py-1 rounded-full text-xs ${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'}`}>
                +{profile.badges.length - 5}
              </div>
            )}
          </div>
        )}

        {/* Follow Button */}
        {!profile.is_own_profile && !isBlocked && (
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleFollow}
              disabled={followLoading}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-medium transition-all ${
                profile.is_following
                  ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                  : 'bg-[#E8334A] text-white'
              } ${followLoading ? 'opacity-50' : ''}`}
            >
              {followLoading ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : profile.is_following ? (
                <>
                  <UserCheck className="w-5 h-5" />
                  {t('social.following')}
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  {t('social.follow')}
                </>
              )}
            </button>
            <MessageProfileButton targetUserId={userId} isDark={isDark} />
            <button 
              onClick={handleShareProfile}
              className={`p-2.5 rounded-xl ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}
            >
              <Share2 className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
            </button>
          </div>
        )}

        {/* Blocked User Banner */}
        {!profile.is_own_profile && isBlocked && (
          <div className={`mt-4 p-4 rounded-xl ${isDark ? 'bg-red-900/30 border border-red-500/30' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
                <Lock className="w-5 h-5 text-red-500" />
              </div>
              <div className="flex-1">
                <p className={`font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                  Utente bloccato
                </p>
                <p className={`text-sm ${isDark ? 'text-red-400/70' : 'text-red-500'}`}>
                  Non vedrai i contenuti di questo utente
                </p>
              </div>
              <button
                onClick={() => setShowBlockConfirm(true)}
                disabled={blockLoading}
                className={`px-4 py-2 rounded-xl text-sm font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-white text-gray-700'} border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
              >
                {blockLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Sblocca'
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Private Profile Notice */}
      {profile.is_private && !profile.can_view_posts && (
        <div className={`mx-4 p-6 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-gray-100'} text-center`}>
          <Lock className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
          <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('social.privateProfile')}
          </h3>
          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {t('social.privateProfileDesc')}
          </p>
        </div>
      )}

      {/* Posts Grid */}
      {profile.can_view_posts && (
        <>
          {/* Grid Tabs - "Tutti" first, "Foto" second */}
          <div className={`flex items-center justify-center border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <button
              onClick={() => setViewMode('all')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 transition-all border-b-2 ${
                viewMode === 'all'
                  ? 'border-[#E8334A] text-[#E8334A]'
                  : `border-transparent ${isDark ? 'text-white/50' : 'text-gray-400'}`
              }`}
            >
              <LayoutGrid className="w-5 h-5" />
              <span className="text-sm font-medium">Tutti</span>
            </button>
            <button
              onClick={() => setViewMode('images')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 transition-all border-b-2 ${
                viewMode === 'images'
                  ? 'border-[#E8334A] text-[#E8334A]'
                  : `border-transparent ${isDark ? 'text-white/50' : 'text-gray-400'}`
              }`}
            >
              <Image className="w-5 h-5" />
              <span className="text-sm font-medium">Foto</span>
            </button>
          </div>

          {/* Content based on viewMode */}
          {viewMode === 'all' ? (
            /* Feed-style posts */
            <div className="px-4 py-4 space-y-4 pb-24">
              {posts.length > 0 ? posts.map((post) => {
                const config = POST_TYPE_CONFIG[post.post_type] || POST_TYPE_CONFIG.motivazione;
                const Icon = config.icon;
                return (
                  <div key={post.post_id} className="glass-card overflow-hidden">
                    {/* Post Header */}
                    <div className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                        {(profile.profile_photo || profile.picture) ? (
                          <img 
                            src={profile.profile_photo || profile.picture} 
                            alt="" 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                            <span className={`font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                              {profile.display_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          {profile.is_premium && (
                            <Crown className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                          )}
                          <span className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {profile.display_name}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${config.badge}`}>
                            <Icon className="w-3 h-3 inline mr-1" />
                            {config.labelKey}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1 bg-[#1d3557]/15 text-[#1d3557]">
                            <Globe className="w-3 h-3" />
                            Pubblico
                          </span>
                        </div>
                        
                        <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                          {formatPostTime(post.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="px-4 pb-3">
                      {(post.text || post.content) && (
                        <p className={`whitespace-pre-wrap ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {post.text || post.content}
                        </p>
                      )}
                      
                      {/* Weight data */}
                      {post.post_type === 'peso' && post.data?.weight && (
                        <div className="mt-3 glass-card-sm p-3 flex items-center gap-3">
                          <Scale className="w-5 h-5 text-[#16A34A]" />
                          <span className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {post.data.weight} kg
                          </span>
                          {post.data.change && (
                            <span className={`text-sm ${post.data.change < 0 ? 'text-[#16A34A]' : 'text-[#E8334A]'}`}>
                              {post.data.change > 0 ? '+' : ''}{post.data.change} kg
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Post Image */}
                    {post.image_url && (
                      <div className="px-4 pb-3">
                        <img 
                          src={post.image_url} 
                          alt="Post"
                          className="w-full rounded-xl object-cover max-h-80 cursor-pointer"
                          onClick={() => openGalleryAtIndex(post.image_url)}
                        />
                      </div>
                    )}

                    {/* Reactions and Comments */}
                    <div className={`px-4 py-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      {/* Reaction buttons — author cannot react to own post */}
                      <div className="flex items-center gap-1 mb-2">
                        {(() => {
                          const isOwnPost = post.user_id === user?.user_id;
                          if (isOwnPost) {
                            const received = REACTION_EMOJIS
                              .map(emoji => ({ emoji, count: getReactionCount(post, emoji) }))
                              .filter(r => r.count > 0);
                            if (received.length === 0) {
                              return <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Nessuna reazione ancora</span>;
                            }
                            return received.map(({ emoji, count }) => (
                              <div
                                key={emoji}
                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full cursor-default ${
                                  isDark ? 'bg-white/5' : 'bg-gray-100'
                                }`}
                                data-testid={`profile-post-reaction-readonly-${emoji}`}
                              >
                                <span className="text-base">{emoji}</span>
                                <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                                  {count}
                                </span>
                              </div>
                            ));
                          }
                          return REACTION_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => handleReaction(post.post_id, emoji)}
                              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full transition-all ${
                                hasReacted(post, emoji)
                                  ? 'bg-[#E8334A]/20 scale-105'
                                  : isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                              }`}
                            >
                              <span className="text-base">{emoji}</span>
                              {getReactionCount(post, emoji) > 0 && (
                                <span className={`text-xs font-medium ${
                                  hasReacted(post, emoji) ? 'text-[#E8334A]' : isDark ? 'text-white/60' : 'text-gray-500'
                                }`}>
                                  {getReactionCount(post, emoji)}
                                </span>
                              )}
                            </button>
                          ));
                        })()}

                        <button 
                          onClick={() => setExpandedComments(prev => ({ ...prev, [post.post_id]: !prev[post.post_id] }))}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ml-auto ${
                            isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          <MessageCircle className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
                          <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                            {post.comments?.length || 0}
                          </span>
                        </button>
                      </div>
                      
                      {/* Comments section */}
                      {expandedComments[post.post_id] && (
                        <div className="mt-3 space-y-3">
                          {/* Comment input */}
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={commentTexts[post.post_id] || ''}
                              onChange={(e) => setCommentTexts(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                              placeholder="Scrivi un commento..."
                              className={`flex-1 px-3 py-2 rounded-xl text-sm ${
                                isDark ? 'bg-white/5 text-white placeholder-white/40' : 'bg-gray-100 text-gray-900'
                              }`}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddComment(post.post_id)}
                            />
                            <button
                              onClick={() => handleAddComment(post.post_id)}
                              disabled={!commentTexts[post.post_id]?.trim() || sendingComment[post.post_id]}
                              className={`p-2 rounded-full ${
                                commentTexts[post.post_id]?.trim() 
                                  ? 'bg-[#E8334A] text-white' 
                                  : isDark ? 'bg-white/10 text-white/40' : 'bg-gray-200 text-gray-400'
                              }`}
                            >
                              {sendingComment[post.post_id] ? (
                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          
                          {/* Comments list */}
                          {post.comments?.length > 0 && (
                            <div className="space-y-2">
                              {post.comments.map((comment) => {
                                const canDelete = comment.user_id === user?.user_id || post.user_id === user?.user_id;
                                return (
                                <div 
                                  key={comment.comment_id}
                                  className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'} ${canDelete ? 'cursor-pointer' : ''}`}
                                  onTouchStart={() => canDelete && handleCommentLongPressStart(comment, post.post_id)}
                                  onTouchEnd={handleCommentLongPressEnd}
                                  onMouseDown={() => canDelete && handleCommentLongPressStart(comment, post.post_id)}
                                  onMouseUp={handleCommentLongPressEnd}
                                  onMouseLeave={handleCommentLongPressEnd}
                                >
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {comment.user_name}
                                    </span>
                                    <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                                      {formatPostTime(comment.created_at)}
                                    </span>
                                  </div>
                                  <p className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                                    {comment.text}
                                  </p>
                                  {/* Reaction buttons — hide pickers from own comment's author */}
                                  {(() => {
                                    const isOwnComment = comment.user_id === user?.user_id;
                                    if (isOwnComment) {
                                      const received = REACTION_EMOJIS
                                        .map(emoji => ({ emoji, reactors: comment.reactions?.[emoji] || [] }))
                                        .filter(r => r.reactors.length > 0);
                                      if (received.length === 0) return null;
                                      return (
                                        <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-testid={`profile-comment-reactions-readonly-${comment.comment_id}`}>
                                          {received.map(({ emoji, reactors }) => (
                                            <div
                                              key={emoji}
                                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                                                isDark ? 'bg-white/5' : 'bg-white border border-gray-200'
                                              }`}
                                            >
                                              <span className="text-sm leading-none">{emoji}</span>
                                              <span className={`text-[10px] font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                                                {reactors.length}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    }
                                    return (
                                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                        {REACTION_EMOJIS.map((emoji) => {
                                          const reactors = comment.reactions?.[emoji] || [];
                                          const userReacted = reactors.includes(user?.user_id);
                                          return (
                                            <button
                                              key={emoji}
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleCommentReaction(post.post_id, comment.comment_id, emoji);
                                              }}
                                              data-testid={`profile-comment-reaction-${emoji}-${comment.comment_id}`}
                                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                                                userReacted
                                                  ? (isDark ? 'bg-[#E8334A]/20 border border-[#E8334A]/40' : 'bg-[#E8334A]/10 border border-[#E8334A]/30')
                                                  : (isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:bg-gray-100 border border-gray-200')
                                              }`}
                                            >
                                              <span className="text-sm leading-none">{emoji}</span>
                                              {reactors.length > 0 && (
                                                <span className={`text-[10px] font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                                                  {reactors.length}
                                                </span>
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                  {canDelete && (
                                    <p className={`text-[10px] mt-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                                      {t('social.holdToDelete') || 'Tieni premuto per eliminare'}
                                    </p>
                                  )}
                                </div>
                              )})}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }) : (
                <div className="py-16 text-center">
                  <LayoutGrid className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                  <p className={isDark ? 'text-white/60' : 'text-gray-500'}>
                    {t('social.noPostsYet')}
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Image grid */
            filteredPosts.length > 0 ? (
              <div className="grid grid-cols-3 gap-0.5 pb-24">
                {filteredPosts.map((post, idx) => (
                  <button
                    key={post.post_id}
                    onClick={() => openGalleryAtIndex(post.image_url)}
                    className="relative aspect-square bg-gray-900 overflow-hidden group"
                  >
                    <img 
                      src={post.image_url} 
                      alt="" 
                      className="w-full h-full object-cover group-hover:opacity-75 transition-opacity"
                    />
                    
                    {/* Hover overlay with stats */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <div className="flex items-center gap-1 text-white">
                        <Heart className="w-5 h-5" fill="white" />
                        <span className="font-semibold">{post.likes_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-1 text-white">
                        <MessageCircle className="w-5 h-5" fill="white" />
                        <span className="font-semibold">{post.comments_count || 0}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <Image className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                <p className={isDark ? 'text-white/60' : 'text-gray-500'}>
                  Nessuna foto
                </p>
              </div>
            )
          )}
        </>
      )}

      {/* Post Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedPost(null)}
        >
          <div 
            className={`w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Post Image — multi-image carousel when available */}
            {(selectedPost.image_url || (Array.isArray(selectedPost.image_urls) && selectedPost.image_urls.length > 0)) && (
              <PostImage post={selectedPost} maxHeightClass="max-h-[60vh]" className="w-full" />
            )}
            
            {/* Post Content */}
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                  {profile.profile_photo ? (
                    <img src={profile.profile_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-300">
                      <span className="text-gray-600 font-bold">
                        {profile.display_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {profile.display_name}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {new Date(selectedPost.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              {selectedPost.content && (
                <p className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  {selectedPost.content}
                </p>
              )}
              
              <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/10">
                <div className="flex items-center gap-1.5">
                  <Heart className="w-5 h-5 text-[#E8334A]" fill="#E8334A" />
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{selectedPost.likes_count || 0}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MessageCircle className={`w-5 h-5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{selectedPost.comments_count || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Followers Modal */}
      {showFollowers && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center"
          onClick={() => setShowFollowers(false)}
        >
          <div 
            className={`w-full sm:max-w-md max-h-[70vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`sticky top-0 p-4 border-b ${isDark ? 'border-white/10 bg-[#1a1a2e]' : 'border-gray-200 bg-white'}`}>
              <h3 className={`text-lg font-semibold text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('social.followers')}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {followersList.length > 0 ? followersList.map((user) => (
                <div 
                  key={user.user_id}
                  className="flex items-center justify-between"
                >
                  <button 
                    onClick={() => {
                      setShowFollowers(false);
                      navigate(`/app/utente/${user.user_id}`);
                    }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-200">
                      {user.profile_photo ? (
                        <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className={`font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                            {(user.display_name || user.name)?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-left">
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.display_name || user.name}
                      </p>
                    </div>
                  </button>
                  {user.user_id !== profile.user_id && (
                    <button
                      className={`px-4 py-1.5 rounded-lg text-sm font-medium ${
                        user.is_following
                          ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                          : 'bg-[#E8334A] text-white'
                      }`}
                    >
                      {user.is_following ? t('social.following') : t('social.follow')}
                    </button>
                  )}
                </div>
              )) : (
                <p className={`text-center py-8 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  Nessun follower
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Following Modal */}
      {showFollowing && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center"
          onClick={() => setShowFollowing(false)}
        >
          <div 
            className={`w-full sm:max-w-md max-h-[70vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`sticky top-0 p-4 border-b ${isDark ? 'border-white/10 bg-[#1a1a2e]' : 'border-gray-200 bg-white'}`}>
              <h3 className={`text-lg font-semibold text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('social.followingTab')}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {followingList.length > 0 ? followingList.map((user) => (
                <div 
                  key={user.user_id}
                  className="flex items-center justify-between"
                >
                  <button 
                    onClick={() => {
                      setShowFollowing(false);
                      navigate(`/app/utente/${user.user_id}`);
                    }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-200">
                      {user.profile_photo ? (
                        <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className={`font-bold ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                            {(user.display_name || user.name)?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-left">
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.display_name || user.name}
                      </p>
                    </div>
                  </button>
                  <button
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium ${
                      isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {t('social.following')}
                  </button>
                </div>
              )) : (
                <p className={`text-center py-8 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  Non segui nessuno
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom padding for safe area */}
      <div className="h-24" />

      {/* Fullscreen Image Viewer */}
      {/* Fullscreen Image Gallery with Swipe */}
      {fullscreenImageIndex !== null && postsWithImages[fullscreenImageIndex] && (
        <div 
          className="fixed inset-0 z-[500] bg-black flex items-center justify-center"
          onClick={() => setFullscreenImageIndex(null)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <button 
            onClick={(e) => { e.stopPropagation(); setFullscreenImageIndex(null); }}
            className="absolute right-4 w-10 h-10 rounded-full bg-[#E8334A] flex items-center justify-center z-10"
            style={{ top: 'calc(env(safe-area-inset-top, 12px) + 12px)' }}
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          {/* Image counter */}
          <div 
            className="absolute left-4 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm font-medium z-10"
            style={{ top: 'calc(env(safe-area-inset-top, 12px) + 16px)' }}
          >
            {fullscreenImageIndex + 1} / {postsWithImages.length}
          </div>
          
          {/* Previous button */}
          {fullscreenImageIndex > 0 && (
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                setFullscreenImageIndex(prev => prev - 1);
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center z-10"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          
          {/* Next button */}
          {fullscreenImageIndex < postsWithImages.length - 1 && (
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                setFullscreenImageIndex(prev => prev + 1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center z-10"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}
          
          {/* Image */}
          <img 
            src={postsWithImages[fullscreenImageIndex].image_url} 
            alt="Fullscreen"
            className="max-w-full max-h-full object-contain select-none"
            onClick={e => e.stopPropagation()}
            draggable={false}
          />
        </div>
      )}

      {/* Share Sheet Modal */}
      {showShareSheet && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-end justify-center"
          onClick={() => setShowShareSheet(false)}
        >
          <div 
            className={`w-full max-w-md rounded-t-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'} p-4`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 rounded-full bg-gray-400 mx-auto mb-4" />
            <h3 className={`text-lg font-semibold mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Condividi profilo
            </h3>
            <div className="space-y-2">
              <button
                onClick={copyProfileLink}
                className={`w-full flex items-center gap-3 p-3 rounded-xl ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}
              >
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                <span>{copied ? 'Copiato!' : 'Copia link'}</span>
              </button>
            </div>
            <button
              onClick={() => setShowShareSheet(false)}
              className={`w-full mt-4 py-3 rounded-xl font-medium ${isDark ? 'bg-white/5 text-white/70' : 'bg-gray-200 text-gray-700'}`}
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Delete Comment Confirmation Modal */}
      {longPressComment && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLongPressComment(null)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'} p-5`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Elimina commento
                </h3>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  Questa azione non può essere annullata
                </p>
              </div>
            </div>
            
            <div className={`p-3 rounded-xl mb-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <p className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                "{longPressComment.text}"
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setLongPressComment(null)}
                className={`flex-1 py-2.5 rounded-xl font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}
              >
                Annulla
              </button>
              <button
                onClick={() => handleDeleteComment(longPressComment.postId, longPressComment.comment_id)}
                className="flex-1 py-2.5 rounded-xl font-medium bg-red-500 text-white"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Confirmation Modal */}
      {showBlockConfirm && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowBlockConfirm(false)}
        >
          <div 
            className={`w-full max-w-sm rounded-2xl ${isDark ? 'bg-[#1a1a2e]' : 'bg-white'} p-5`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-full ${isBlocked ? 'bg-green-500/20' : 'bg-red-500/20'} flex items-center justify-center`}>
                <UserX className={`w-6 h-6 ${isBlocked ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {isBlocked 
                    ? (t('social.unblockTitle') || 'Sblocca utente') 
                    : (t('social.blockTitle') || 'Blocca utente')}
                </h3>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {profile?.display_name}
                </p>
              </div>
            </div>
            
            <p className={`text-sm mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              {isBlocked 
                ? (t('social.unblockText') || 'Potrai vedere di nuovo i post di questo utente.')
                : (t('social.blockText') || 'Non vedrai più i post di questo utente e non potrà vedere i tuoi.')}
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowBlockConfirm(false)}
                className={`flex-1 py-2.5 rounded-xl font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'}`}
              >
                {t('common.cancel') || 'Annulla'}
              </button>
              <button
                onClick={handleBlockUser}
                disabled={blockLoading}
                className={`flex-1 py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 ${
                  isBlocked ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                }`}
              >
                {blockLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isBlocked ? (
                  t('social.unblock') || 'Sblocca'
                ) : (
                  t('social.block') || 'Blocca'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avatar fullscreen zoom */}
      {showAvatarZoom && (profile.profile_photo || profile.picture || profile.profilePhoto || profile.avatar) && (
        <div
          className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center"
          onClick={() => setShowAvatarZoom(false)}
          data-testid="avatar-zoom-overlay"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowAvatarZoom(false); }}
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
            className="absolute right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center backdrop-blur-sm"
            aria-label={t('common.close', 'Chiudi')}
            data-testid="avatar-zoom-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={profile.profile_photo || profile.picture || profile.profilePhoto || profile.avatar}
            alt={profile.display_name || ''}
            className="max-w-[92vw] max-h-[85vh] object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// "Send message" button on the public profile.
// Wires `useCanMessage` to gate the button and navigates to a small
// composer page that creates the conversation on first send.
// ─────────────────────────────────────────────────────────────────
function MessageProfileButton({ targetUserId, isDark }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data } = useCanMessage(targetUserId);
  if (!data) return null;
  if (!data.allowed) return null;
  const label =
    data.mode === 'request'
      ? t('messages.requestMode', 'Request')
      : t('messages.sendMessage');
  return (
    <button
      onClick={() => navigate(`/app/messaggi/nuova/${targetUserId}`)}
      data-testid="profile-message-btn"
      aria-label={label}
      title={label}
      className={`p-2.5 rounded-xl ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}
    >
      <Send className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-900'}`} />
    </button>
  );
}
